import type {
    Form as FormComponent,
    StyleFunction,
} from "@venuepos/react-common";
import {
    Button,
    CheckBox,
    InputControl,
    Loading,
    Picker,
    Surface,
    TextInput,
    useThemedStyle,
} from "@venuepos/react-common";
import type { GQApprovalTokenInput } from "graphql-sdk";
import {
    useApprovalTokenFormQuery,
    useApprovalTokenGroupsQuery,
} from "graphql-sdk";
import { useTranslation } from "locales";
import React from "react";

export function Form(props: {
    form: FormComponent<GQApprovalTokenInput>;
    onSubmit: () => void;
    submitButton: [string, string];
}) {
    const [t] = useTranslation();
    const [{ values, errors }, { setValue, handleSubmit }] = props.form;
    const styles = useThemedStyle(styleFunc);
    const { data, loading } = useApprovalTokenFormQuery();
    const { data: tokenGroupData, loading: tokenGroupLoading } =
        useApprovalTokenGroupsQuery();

    if (loading || tokenGroupLoading) {
        return <Loading />;
    }

    if (!values || !data) {
        return null;
    }

    return (
        <>
            <Surface style={styles.surfaceContainer}>
                <InputControl
                    error={errors.name}
                    description={t(
                        "backoffice.approval_token.name_description",
                        "The name of the approval token"
                    )}
                >
                    <TextInput
                        label={t("common.name", "Name")}
                        placeholder={t(
                            "backoffice.common.enter_name",
                            "Enter name"
                        )}
                        required={true}
                        defaultValue={values.name || ""}
                        onChangeText={text => setValue("name", text)}
                    />
                </InputControl>

                <InputControl
                    error={errors.active}
                    description={t(
                        "backoffice.approval_token.active_description",
                        "Should the approval token be active or not"
                    )}
                >
                    <CheckBox
                        label={t("common.active", "Active")}
                        value={values.active}
                        onValueChange={checked => setValue("active", checked)}
                        testID="active"
                    />
                </InputControl>

                <InputControl
                    error={errors.value}
                    description={t(
                        "backoffice.approval_token.value_description",
                        "The value of the approval token"
                    )}
                >
                    <TextInput
                        label={t("backoffice.approval_token.value", "Value")}
                        placeholder={t(
                            "backoffice.approval_token.enter_value",
                            "Enter value"
                        )}
                        required={true}
                        defaultValue={values.value || ""}
                        onChangeText={text => setValue("value", text)}
                    />
                </InputControl>

                <InputControl
                    error={errors.type}
                    description={t(
                        "backoffice.approval_token.type_description",
                        "The type of approval token."
                    )}
                >
                    <Picker
                        label={t("common.type", "Type")}
                        required={true}
                        onValueChange={value => {
                            setValue("type", value);
                        }}
                        selectedValue={values.type || ""}
                    >
                        <Picker.Item
                            label={t(
                                "backoffice.approval_token.choose_type",
                                "Choose type"
                            )}
                            value=""
                        />
                        <Picker.Item
                            key="CODE"
                            value="CODE"
                            label={t(
                                "backoffice.approval_token.types.CODE",
                                "Code"
                            )}
                        />
                        <Picker.Item
                            key="CARD"
                            value="CARD"
                            label={t(
                                "backoffice.approval_token.types.CARD",
                                "Card"
                            )}
                        />
                    </Picker>
                </InputControl>

                <InputControl
                    error={errors.type}
                    description={t(
                        "backoffice.approval_token.approval_token_group_description",
                        "Optional approval token group"
                    )}
                >
                    <Picker
                        label={t(
                            "backoffice.approval_token.approval_token_group",
                            "Approval token group"
                        )}
                        onValueChange={value => {
                            setValue("approvalTokenGroupId", value);
                        }}
                        selectedValue={values.approvalTokenGroupId}
                    >
                        <Picker.Item
                            label={t(
                                "backoffice.approval_token.choose_approval_token_group",
                                "Choose approval token group"
                            )}
                            value=""
                        />
                        {tokenGroupData.approvalTokenGroups.data.map(item => (
                            <Picker.Item
                                key={item.name}
                                value={item.id}
                                label={item.name}
                            />
                        ))}
                    </Picker>
                </InputControl>

                <Button onPress={handleSubmit(props.onSubmit)}>
                    {t(props.submitButton[0], props.submitButton[1])}
                </Button>
            </Surface>
        </>
    );
}

const styleFunc: StyleFunction = theme => ({
    surfaceContainer: {
        marginVertical: theme.spacingScale / 2,
    },
});
